import { Link } from 'react-router-dom';

const RATES = [
  { age: '30', coverage: '$500,000', term: '20-yr', male: '$27', female: '$22' },
  { age: '35', coverage: '$500,000', term: '20-yr', male: '$31', female: '$26' },
  { age: '35', coverage: '$1,000,000', term: '20-yr', male: '$50', female: '$42' },
  { age: '40', coverage: '$500,000', term: '20-yr', male: '$44', female: '$37' },
  { age: '40', coverage: '$750,000', term: '20-yr', male: '$62', female: '$52' },
  { age: '45', coverage: '$500,000', term: '20-yr', male: '$67', female: '$55' },
  { age: '50', coverage: '$500,000', term: '20-yr', male: '$105', female: '$86' },
];

const CARRIERS = [
  { name: 'Mutual of Omaha', rating: 'A+', note: 'Best for seniors & final expense' },
  { name: 'Protective Life', rating: 'A+', note: 'Lowest term rates in Indiana' },
  { name: 'North American', rating: 'A+', note: 'IUL & indexed products' },
  { name: 'Transamerica', rating: 'A', note: 'No-exam up to $2M' },
  { name: 'Foresters Financial', rating: 'A', note: 'Fraternal benefits included' },
  { name: 'Americo', rating: 'A', note: 'Guaranteed issue options' },
];

export default function HomePricingSection() {
  return (
    <section id="pricing-summary" className="py-16 md:py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/15 text-[#C26638] text-[11px] font-bold uppercase tracking-widest rounded-full mb-4 border border-[#D97642]/25">
            <i className="ri-price-tag-3-line text-[12px]"></i>
            Actual Indiana Rates
          </span>
          <h2 className="text-[28px] md:text-[36px] font-bold text-[#1A3A52] leading-[1.2] mb-3">
            How Much Does Life Insurance<br className="hidden md:block" /> Cost in Indiana?
          </h2>
          <p className="text-[15px] md:text-[16px] text-gray-500 leading-[1.7] max-w-[580px] mx-auto">
            Indiana rates run <strong>8–12% below the national average</strong>. Rates below reflect real market prices for healthy, non-smoking Indiana residents across the 20+ carriers we compare.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

          {/* Rate Table */}
          <div className="lg:col-span-3">
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-4 py-3.5 text-left font-semibold text-[13px]">Age</th>
                    <th className="px-4 py-3.5 text-left font-semibold text-[13px]">Coverage</th>
                    <th className="px-4 py-3.5 text-left font-semibold text-[13px]">Term</th>
                    <th className="px-4 py-3.5 text-center font-semibold text-[13px]">Male</th>
                    <th className="px-4 py-3.5 text-center font-semibold text-[13px]">Female</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {RATES.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF9]'}>
                      <td className="px-4 py-3 font-semibold text-[#1A3A52] text-[13px]">{row.age}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-[13px]">{row.coverage}</td>
                      <td className="px-4 py-3 text-[#4A5568] text-[13px]">{row.term}</td>
                      <td className="px-4 py-3 text-center font-bold text-[#D97642] text-[13px]">{row.male}/mo</td>
                      <td className="px-4 py-3 text-center font-bold text-[#D97642] text-[13px]">{row.female}/mo</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-gray-400 italic mt-3 px-1">
              *Estimated rates for healthy non-smokers at Preferred Plus classification. Actual rates vary by carrier and individual health profile.
            </p>

            {/* CTA row */}
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/instant-quote"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3.5 bg-[#D97642] text-white text-[14px] font-bold rounded-xl hover:bg-[#C26638] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line text-[16px]"></i>
                Get My Exact Rate — Free
              </Link>
              <Link
                to="/compare"
                className="inline-flex items-center justify-center gap-2 py-3.5 px-5 bg-[#F7F9FB] border border-gray-200 text-[#1A3A52] text-[14px] font-semibold rounded-xl hover:border-[#D97642] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-scales-3-line text-[16px]"></i>
                Compare Term vs. Whole Life
              </Link>
            </div>
          </div>

          {/* Carrier Context */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#F7F9FB] rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-[#D97642]/10 rounded-lg">
                  <i className="ri-building-2-line text-[#D97642] text-[16px]"></i>
                </div>
                <h3 className="text-[14px] font-bold text-[#1A3A52]">Carriers We Compare</h3>
              </div>
              <div className="space-y-3">
                {CARRIERS.map(c => (
                  <div key={c.name} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <span className="inline-block text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                        {c.rating}
                      </span>
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold text-[#1A3A52]">{c.name}</div>
                      <div className="text-[11px] text-gray-500">{c.note}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-4 pt-3 border-t border-gray-200">
                + 14 more A-rated carriers. We shop all of them to find your lowest rate.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-xl p-5 text-white">
              <div className="w-9 h-9 flex items-center justify-center bg-[#D97642]/20 rounded-lg mb-3">
                <i className="ri-hand-coin-line text-[#D97642] text-[18px]"></i>
              </div>
              <h3 className="text-[15px] font-bold text-white mb-2">Our service is free to you</h3>
              <p className="text-[13px] text-white/70 leading-[1.7] mb-4">
                We&apos;re compensated by the carriers — at no extra cost to you. You pay the same rate whether you buy direct or through us. You just get 20+ options instead of one.
              </p>
              <a href="tel:+13176482983" className="flex items-center gap-2 text-[#D97642] text-[13px] font-semibold hover:text-[#F0A070] transition-colors cursor-pointer">
                <i className="ri-phone-fill text-[14px]"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}